/**
 * Service / repair domain rules (pure: no imports, no I/O).
 *
 * The screens use them only to explain why an action is disabled. The rules
 * that decide live on the server (lib/service/domain.js, kept equal to these
 * by tests/unit/service.test.js).
 */

/* ------------------------------------------------------------------ stages */

/** Workbench stages in board order (PRD §6: DIAGNOSING → … → CLOSED). */
export const STAGES = [
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'awaiting_approval', label: 'Awaiting approval' },
  { key: 'in_repair', label: 'In repair' },
  { key: 'qc', label: 'QC' },
  { key: 'ready_for_return', label: 'Ready for return' },
  { key: 'closed', label: 'Closed' },
];

export const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));

/** Badge variant per workbench stage. */
export const STAGE_VARIANT = {
  diagnosis: 'warning',
  awaiting_approval: 'warning',
  in_repair: 'primary',
  qc: 'secondary',
  ready_for_return: 'success',
  closed: 'default',
};

/** Ticket statuses (PRD §6, before a work order exists and after). */
export const TICKET_STATUS = {
  open: { label: 'New', variant: 'primary' },
  triaged: { label: 'Triaged', variant: 'warning' },
  pickup_scheduled: { label: 'Pickup scheduled', variant: 'warning' },
  device_received: { label: 'Device received', variant: 'success' },
  in_service: { label: 'In service', variant: 'primary' },
  closed: { label: 'Closed', variant: 'default' },
};

export const PRIORITY = {
  urgent: { label: 'Urgent', variant: 'error' },
  high: { label: 'High', variant: 'warning' },
  normal: { label: 'Normal', variant: 'default' },
};

export const CHANNEL_LABEL = { storefront: 'Storefront', walk_in: 'Walk-in', phone: 'Phone', whatsapp: 'WhatsApp', marketplace: 'Marketplace' };

/** What the customer allowed the technician to do with their storage. */
export const STORAGE_ACCESS = {
  none: { label: 'No storage access', detail: 'Do not boot the customer OS. Test with the service USB only.' },
  boot_only: { label: 'Boot to login screen only', detail: 'The device may boot to its login screen. Do not sign in or open files.' },
  supervised: { label: 'Supervised access', detail: 'The customer unlocks the device in person; a backup was requested before any storage work.' },
};

export const SLA_LABEL = {
  on_track: { label: 'On track', variant: 'success' },
  at_risk: { label: 'At risk', variant: 'warning' },
  breached: { label: 'Breached', variant: 'error' },
  done: { label: 'Done', variant: 'default' },
};

export const ESTIMATE_STATUS = {
  draft: { label: 'Draft', variant: 'default' },
  sent: { label: 'Awaiting customer', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
  declined: { label: 'Declined', variant: 'error' },
  superseded: { label: 'Superseded', variant: 'outline' },
};

export const CLAIM_STATUS = {
  open: { label: 'Open', variant: 'primary' },
  reopened: { label: 'Reopened as ticket', variant: 'success' },
  escalated: { label: 'Escalated', variant: 'error' },
  rejected: { label: 'Not eligible', variant: 'default' },
};

/** Common laptop fault codes for the diagnosis picker. */
export const FAULT_CODES = [
  { value: 'PWR-01', label: 'PWR-01 No power / DC-in' },
  { value: 'PWR-02', label: 'PWR-02 Board short' },
  { value: 'DSP-01', label: 'DSP-01 Panel damage' },
  { value: 'DSP-03', label: 'DSP-03 Flicker / eDP cable' },
  { value: 'KBD-02', label: 'KBD-02 Keyboard matrix' },
  { value: 'BAT-01', label: 'BAT-01 Battery worn / not charging' },
  { value: 'THM-02', label: 'THM-02 Overheating / fan' },
  { value: 'STO-02', label: 'STO-02 SSD failing' },
  { value: 'MEC-04', label: 'MEC-04 Hinge / chassis' },
  { value: 'LIQ-01', label: 'LIQ-01 Liquid damage' },
  { value: 'NET-01', label: 'NET-01 Wi-Fi / Bluetooth' },
];

/**
 * Guards: each returns null when the move is allowed, or a human sentence
 * explaining why not. Named so the transition table stays declarative.
 */
const GUARDS = {
  quoteSent: (job) => (latestEstimate(job) && latestEstimate(job).status !== 'draft' ? null : 'Send a quote to the customer first.'),
  quoteApproved: (job) => (isApproved(job) ? null : 'The customer has not approved the latest quote.'),
  quoteDeclined: (job) => {
    const est = latestEstimate(job);
    return est && est.status === 'declined' ? null : 'Only a declined quote goes straight to return.';
  },
  qcPassed: (job) => (qcComplete(job) ? null : 'Every QC check must pass first.'),
  returnAcknowledged: (job) => (job.returnAck ? null : 'Record the customer’s return acknowledgement first.'),
  always: () => null,
};

/**
 * Allowed moves: from → [{ to, guard }]. Anything not listed is not allowed.
 * awaiting_approval → diagnosis is a quote revision; → ready_for_return is the
 * "declined, arrange safe return" branch of the approval sequence (PRD §8).
 */
export const TRANSITIONS = {
  diagnosis: [{ to: 'awaiting_approval', guard: 'quoteSent' }],
  awaiting_approval: [
    { to: 'in_repair', guard: 'quoteApproved' },
    { to: 'diagnosis', guard: 'always' },
    { to: 'ready_for_return', guard: 'quoteDeclined' },
  ],
  in_repair: [{ to: 'qc', guard: 'quoteApproved' }],
  qc: [
    { to: 'in_repair', guard: 'always' },
    { to: 'ready_for_return', guard: 'qcPassed' },
  ],
  ready_for_return: [{ to: 'closed', guard: 'returnAcknowledged' }],
  closed: [],
};

/** Stages a job may be moved to from where it is (ignoring guards). */
export function nextStages(from) {
  return (TRANSITIONS[from] || []).map((t) => t.to);
}

/** { ok: true } or { ok: false, reason } for moving `job` to stage `to`. */
export function canTransition(job, to) {
  const rule = (TRANSITIONS[job && job.stage] || []).find((t) => t.to === to);
  if (!rule) return { ok: false, reason: `A job cannot move from ${STAGE_LABEL[job && job.stage] || 'this stage'} to ${STAGE_LABEL[to] || to}.` };
  const reason = GUARDS[rule.guard](job);
  return reason ? { ok: false, reason } : { ok: true };
}

/* ------------------------------------------------------------------ quotes */

/** Latest estimate revision of a job (highest version), or null. */
export function latestEstimate(job) {
  const list = (job && job.estimates) || [];
  return list.reduce((best, e) => (!best || e.version > best.version ? e : best), null);
}

/** Billable repair is allowed only when the latest revision is approved. */
export function isApproved(job) {
  const est = latestEstimate(job);
  return Boolean(est && est.status === 'approved');
}

/**
 * Quote totals in integer paise. Lines: { lineType: 'part'|'labour', quantity,
 * unitPricePaise, taxRate (percent) }. Tax is rounded per line, like an invoice.
 */
export function quoteTotals(lines = []) {
  let partsPaise = 0;
  let labourPaise = 0;
  let taxPaise = 0;
  for (const l of lines) {
    const qty = Math.max(0, Math.trunc(Number(l.quantity) || 0));
    const unit = Math.max(0, Math.trunc(Number(l.unitPricePaise) || 0));
    const net = qty * unit;
    if (l.lineType === 'labour') labourPaise += net;
    else partsPaise += net;
    taxPaise += Math.round((net * (Number(l.taxRate) || 0)) / 100);
  }
  return { partsPaise, labourPaise, taxPaise, totalPaise: partsPaise + labourPaise + taxPaise };
}

/* --------------------------------------------------------------------- QC */

export const QC_CHECKS = [
  { code: 'POWER_ON', label: 'Powers on and boots to OS' },
  { code: 'DISPLAY', label: 'Display: no flicker, dead pixels or bleed' },
  { code: 'KEYBOARD', label: 'Keyboard and touchpad: every key registers' },
  { code: 'CHARGING', label: 'Charges from the supplied adapter' },
  { code: 'THERMALS', label: '15-minute stress test under 90 °C' },
  { code: 'PORTS', label: 'USB, audio and Wi-Fi working' },
  { code: 'COSMETIC', label: 'No new cosmetic damage vs intake photos' },
];

export function qcComplete(job) {
  const checks = (job && job.qc) || {};
  return QC_CHECKS.every((c) => checks[c.code] === 'pass');
}

/* --------------------------------------------------------------------- SLA */

const HOUR = 3600 * 1000;

/** Stages where the repair turnaround clock has stopped (the repair is done). */
export const SLA_STOPPED = ['ready_for_return', 'closed'];

/**
 * Repair-turnaround SLA of a job: 'done' (ready for return or closed),
 * 'breached' (past due), 'at_risk' (25% of the window or 4 hours left, or
 * less), else 'on_track'.
 */
export function slaStatus(job, now = Date.now()) {
  if (!job || SLA_STOPPED.includes(job.stage)) return 'done';
  const due = new Date(job.dueAt).getTime();
  const opened = new Date(job.openedAt).getTime();
  if (Number.isNaN(due)) return 'on_track';
  const left = due - now;
  if (left < 0) return 'breached';
  const window = Number.isNaN(opened) ? 0 : due - opened;
  if (left <= 4 * HOUR || (window > 0 && left <= window * 0.25)) return 'at_risk';
  return 'on_track';
}

export const isSlaAtRisk = (job, now) => ['at_risk', 'breached'].includes(slaStatus(job, now));

/** Hours left until due (negative when breached), rounded down. */
export function hoursLeft(job, now = Date.now()) {
  const ms = new Date(job.dueAt).getTime() - now;
  return ms >= 0 ? Math.floor(ms / HOUR) : -Math.ceil(-ms / HOUR);
}

/* -------------------------------------------------------------- capacity */

/** Utilisation % of a technician: planned hours of open jobs / capacity. */
export function utilisation(tech, jobs) {
  const planned = jobs
    .filter((j) => j.technicianId === tech.id && j.stage !== 'closed')
    .reduce((sum, j) => sum + (Number(j.plannedHours) || 0), 0);
  const cap = Number(tech.capacityHours) || 0;
  return { plannedHours: planned, capacityHours: cap, percent: cap ? Math.round((planned / cap) * 100) : 0 };
}

/* ------------------------------------------------------------- custody */

/** Shape check for a 6-digit handover code (the server verifies it). */
export const isValidOtp = (otp) => /^\d{6}$/.test(String(otp || '').trim());

/** Show only the last 4 characters of a serial number. */
export function maskSerial(serial) {
  const s = String(serial || '');
  if (s.length <= 4) return s;
  return `${'•'.repeat(Math.min(6, s.length - 4))}${s.slice(-4)}`;
}

/* ------------------------------------------------------------- warranty */

const DAY = 24 * HOUR;
/** Fallback only: the live DOA window is the platform returns_warranty policy (lib/store/policyTerms), passed as doaDays. */
export const DOA_DAYS = 10;
export const REPEAT_REPAIR_DAYS = 90;

/** Exclusions that void a repair warranty claim. */
export const WARRANTY_EXCLUSIONS = {
  liquid_damage: 'New liquid damage is not covered.',
  physical_damage: 'New physical damage is not covered.',
  seal_broken: 'The service seal was broken by a third party.',
};

/**
 * Eligibility of a claim against a warranty:
 * { eligible, reasons[], doa, daysSinceStart }.
 */
export function checkEligibility(warranty, { claimedAt = Date.now(), exclusion = null, doaDays = DOA_DAYS } = {}) {
  const reasons = [];
  if (!warranty) return { eligible: false, reasons: ['No warranty found for this device.'], doa: false, daysSinceStart: null };
  const at = new Date(claimedAt).getTime();
  const starts = new Date(warranty.startsAt).getTime();
  const ends = new Date(warranty.endsAt).getTime();
  if (warranty.status === 'void') reasons.push('This warranty has been voided.');
  if (at > ends) reasons.push('The warranty period has ended.');
  if (at < starts) reasons.push('The warranty has not started yet.');
  if (exclusion && WARRANTY_EXCLUSIONS[exclusion]) reasons.push(WARRANTY_EXCLUSIONS[exclusion]);
  const daysSinceStart = Math.floor((at - starts) / DAY);
  return { eligible: reasons.length === 0, reasons, doa: daysSinceStart >= 0 && daysSinceStart <= doaDays, daysSinceStart };
}

/** True when the same device had another repair closed within 90 days before `at`. */
export function isRepeatRepair(deviceId, closedJobs, at = Date.now()) {
  const t = new Date(at).getTime();
  return closedJobs.some((j) => {
    if (j.deviceId !== deviceId || !j.completedAt) return false;
    const done = new Date(j.completedAt).getTime();
    return done <= t && t - done <= REPEAT_REPAIR_DAYS * DAY;
  });
}

/* ----------------------------------------------------------------- parts */

/** Free stock and whether it is at/below the reorder level. */
export function partStock(part) {
  const free = (Number(part.onHand) || 0) - (Number(part.reserved) || 0);
  return { free, low: free <= (Number(part.reorderLevel) || 0) };
}
